import React, { useState } from "react";
import {
  Toolbar,
  Typography,
  Box,
  IconButton,
  Tooltip,
  alpha,
  styled,
  InputBase,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import { darken } from "polished";

const StyledToolbar = styled(Toolbar)(({ theme, numSelected }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1),
  ...(numSelected > 0 && {
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.action.activatedOpacity
    ),
  }),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  minHeight: "70px",
  borderBottom: "1px solid rgba(224, 224, 224, 0.4)",
}));

const Search = styled("div")(({ theme }) => ({
  borderRadius: 2,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  width: "100%",
  maxWidth: "400px",
  "&:hover": {
    borderColor: theme.palette.primary.main,
  },
  "&:focus-within": {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.text.secondary,
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1.5, 1, 1.5, 0),
    transition: theme.transitions.create("width"),
    width: "100%",
  },
}));

const TicketToolbar = ({
  numSelected,
  searchQuery,
  onSearchChange,
  priorityFilter,
  onPriorityChange,
  onDelete,
  onExportCSV,
  spaceId,
  onCopyUrl,
}) => {
  const [localSearch, setLocalSearch] = useState(searchQuery || "");

  const handleSearchKeyPress = (event) => {
    if (event.key === "Enter") {
      onSearchChange(localSearch);
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setLocalSearch(value);
    // Auto-search with debounce would be better, but for now trigger on Enter
  };

  const handleSearchBlur = () => {
    if (localSearch !== searchQuery) {
      onSearchChange(localSearch);
    }
  };

  return (
    <StyledToolbar numSelected={numSelected}>
      <Box display="flex" alignItems="center" gap={2} flex={1}>
        {numSelected > 0 ? (
          <Typography
            color="inherit"
            variant="subtitle1"
            sx={{ fontWeight: 500 }}
          >
            {numSelected} selected
          </Typography>
        ) : (
          <>
            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search tickets..."
                value={localSearch}
                onChange={handleSearchChange}
                onKeyPress={handleSearchKeyPress}
                onBlur={handleSearchBlur}
                inputProps={{ "aria-label": "search tickets" }}
              />
            </Search>

            <FormControl variant="outlined" size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                value={priorityFilter || ""}
                onChange={(e) => onPriorityChange(e.target.value)}
                label="Priority"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Low">Low</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </>
        )}
      </Box>

      <Box display="flex" alignItems="center" gap={1}>
        {numSelected > 0 ? (
          <Tooltip title="Delete selected">
            <IconButton aria-label="Delete" size="large" onClick={onDelete}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <>
            <Tooltip title="Copy public ticket URL">
              <IconButton
                aria-label="Copy public URL"
                size="medium"
                onClick={onCopyUrl}
                sx={{
                  borderRadius: "8px",
                  p: 1,
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.08),
                  "&:hover": {
                    backgroundColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.12),
                  },
                }}
              >
                <LinkIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export to CSV">
              <IconButton
                aria-label="Export CSV"
                size="medium"
                onClick={onExportCSV}
                sx={{
                  borderRadius: "8px",
                  p: 1,
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.08),
                  "&:hover": {
                    backgroundColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.12),
                  },
                }}
              >
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </StyledToolbar>
  );
};

export default TicketToolbar;
